import { CASH_FLOOR, CASH_FLOOR_REGROWTH_PER_TICK, roundCents } from '../content/economy';
import { stepConveyor } from './conveyor';
import type { SimEvent } from './events';
import { stepMachine } from './machine';
import { stepSink } from './sink';
import { stepObservable } from './observable';
import type { SimState } from './state';

/** Runs whether or not anything is subscribed, so upkeep can never strand a player below the floor. */
function regrowTowardCashFloor(state: SimState): void {
  if (state.economy.cash >= CASH_FLOOR) return;
  state.economy.cash = Math.min(
    CASH_FLOOR,
    roundCents(state.economy.cash + CASH_FLOOR_REGROWTH_PER_TICK),
  );
}

export function tick(
  state: SimState,
  random: () => number = Math.random,
): { state: SimState; events: SimEvent[] } {
  const events: SimEvent[] = [];

  for (const step of state.evalOrder) {
    switch (step.kind) {
      case 'sink':
        events.push(...stepSink(state, state.sinks[step.id]));
        break;
      case 'conveyor':
        events.push(...stepConveyor(state, state.conveyors[step.id]));
        break;
      case 'machine':
        events.push(...stepMachine(state, state.machines[step.id]));
        break;
      case 'observable':
        events.push(...stepObservable(state, state.observables[step.id], random));
        break;
    }
  }

  regrowTowardCashFloor(state);

  state.tick += 1;
  return { state, events };
}

export function stepN(
  state: SimState,
  n: number,
  opts: { collectEvents?: boolean; random?: () => number } = {},
): { state: SimState; events: SimEvent[] } {
  const collectEvents = opts.collectEvents ?? true;
  const random = opts.random ?? Math.random;
  const events: SimEvent[] = [];

  for (let i = 0; i < n; i++) {
    const result = tick(state, random);
    if (collectEvents) {
      events.push(...result.events);
    }
  }

  return { state, events };
}
